<script>
  // One feed row. Labels, the verdict a settled row shows ("Approved by you", …),
  // whether it can be acted on or opened for review, and "how long ago" all come
  // from the server — this file only holds sentence structure.
  import { Link } from '@inertiajs/svelte'
  import ClickableImage from '../../components/lightbox/clickable_image.svelte'
  import ClickableVideo from '../../components/lightbox/clickable_video.svelte'
  import { Badge, Button, Icon } from '../../components/ui'

  let { event, isNew = false, canUndo = false, busy = false, actions, onOpen } = $props()

  const THUMB = 'h-14 w-14 object-cover rounded-box'
  const FALLBACK_ICONS = {
    quest_submission_submitted: 'quest_submissions',
    quest_submission_approved: 'check',
    quest_submission_rejected: 'close',
    moment_reported: 'user_reports',
    moment_reviewed: 'check',
    media_upload_failed: 'media_uploads'
  }

  const submission = $derived(event.quest_submission)
  const moment = $derived(event.moment)
  const report = $derived(event.moment_report || event.user_report)
  const upload = $derived(event.media_upload)
  const userKind = $derived(event.kind === 'user_reported' || event.kind === 'user_report_reviewed')
  const openable = $derived(event.openable)
  const momentDeleted = $derived(event.kind === 'moment_reviewed' && !!moment?.discarded_at)
  const adminName = $derived(event.admin?.name ?? 'An admin')

  const rejectionLabel = $derived(submission?.rejection_category_label ?? null)
  const verdict = $derived(event.verdict)
  const actionable = $derived(event.actionable)

  const uploadedText = $derived.by(() => {
    const roles = upload?.uploaded_roles
    if (!roles) return null
    return roles.length > 0 ? `uploaded: ${roles.join(', ')}` : 'nothing arrived'
  })

  // The row opens review mode, but not when the click landed on something
  // interactive (action buttons, user links, media lightboxes).
  const onRowClick = (e) => {
    if (!openable || e.target.closest('a, button, img, video, input, label')) return
    onOpen(event)
  }
</script>

{#snippet person(user)}
  {#if user}
    <Link href={user.url} class="link link-hover font-medium">{user.name || 'Unknown user'}</Link>
  {:else}
    <span>Someone</span>
  {/if}
{/snippet}

{#snippet momentOwner(suffix)}
  {#if moment?.user}
    <Link href={moment.user.url} class="link link-hover font-medium">{moment.user.name || 'a user'}</Link>’s{suffix}
  {:else}
    a{suffix}
  {/if}
{/snippet}

<!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_noninteractive_element_interactions (the Review button is the keyboard path) -->
<li
  class="flex items-start gap-3 px-4 py-3 border-b border-base-300 transition-colors duration-1000 {isNew ? 'bg-primary/10' : ''} {openable
    ? 'cursor-pointer hover:bg-base-200'
    : ''}"
  data-event-id={event.id}
  onclick={onRowClick}
>
  <div class="shrink-0 {actionable ? '' : 'opacity-70'}">
    {#if userKind}
      {#if event.user?.icon_image_url}
        <ClickableImage url={event.user.icon_image_url} title={event.user.name || ''} thumbClass={THUMB} />
      {:else}
        <div class="h-14 w-14 rounded-box bg-base-200 flex items-center justify-center"><Icon name="person" size="lg" class="text-base-content/40" /></div>
      {/if}
    {:else if moment?.video_url}
      <ClickableVideo url={moment.video_url} thumbnailUrl={moment.poster_url} thumbClass={THUMB} />
    {:else if moment?.poster_url}
      <ClickableImage url={moment.poster_url} thumbClass={THUMB} />
    {:else}
      <div class="h-14 w-14 rounded-box bg-base-200 flex items-center justify-center">
        <Icon name={momentDeleted ? 'delete' : FALLBACK_ICONS[event.kind] || 'info'} size="lg" class="text-base-content/40" />
      </div>
    {/if}
  </div>

  <div class="flex-1 min-w-0 {actionable ? '' : 'opacity-70'}">
    <p class="text-sm">
      {#if event.kind === 'quest_submission_submitted'}
        {@render person(event.actor)} submitted <strong>{event.quest_label ?? 'a quest'}</strong>
      {:else if event.kind === 'quest_submission_approved' || event.kind === 'quest_submission_rejected'}
        <strong>{adminName}</strong>
        {event.kind === 'quest_submission_approved' ? 'approved' : 'rejected'}
        {@render person(event.actor)}{#if event.quest_label}’s <strong>{event.quest_label}</strong>{/if}
      {:else if event.kind === 'moment_reported'}
        {@render person(event.actor)} reported
        {#if moment}{@render momentOwner(' moment')}{:else}a moment{/if}
      {:else if event.kind === 'moment_reviewed'}
        <strong>{adminName}</strong>
        {momentDeleted ? 'deleted' : 'approved'}
        {@render momentOwner(' reported moment')}
      {:else if event.kind === 'user_reported'}
        {@render person(event.actor)} reported {@render person(event.user)}
      {:else if event.kind === 'user_report_reviewed'}
        <strong>{adminName}</strong>
        {report?.icon_cleared ? 'cleared the icon of' : 'reviewed reports on'}
        {@render person(event.user)}
      {:else if event.kind === 'media_upload_failed'}
        {@render person(event.actor)}’s upload failed{#if event.quest_label}{' '}for <strong>{event.quest_label}</strong>{/if}
      {:else}
        {event.kind}
      {/if}
    </p>

    <div class="mt-1 flex items-center gap-2 flex-wrap text-xs text-base-content/60">
      <span title={event.occurred_label}>{event.ago} ago</span>

      {#if event.kind === 'quest_submission_submitted' && moment?.media_type}
        <Badge variant="ghost" size="sm">{moment.media_type}</Badge>
      {/if}
      {#if event.kind === 'quest_submission_rejected'}
        {#if rejectionLabel}<Badge variant="error" soft size="sm">{rejectionLabel}</Badge>{/if}
        {#if submission?.rejection_reason}<span>“{submission.rejection_reason}”</span>{/if}
      {/if}
      {#if (event.kind === 'quest_submission_approved' || event.kind === 'quest_submission_rejected') && submission?.example}
        <Badge variant="primary" soft size="sm">Example</Badge>
      {/if}
      {#if event.kind === 'moment_reported' || event.kind === 'user_reported'}
        {#if report?.reason}<Badge variant="warning" soft size="sm">{report.reason_label}</Badge>{/if}
        {#if report?.details}<span>“{report.details}”</span>{/if}
        {#if event.kind === 'moment_reported' && moment?.reports_count > 1}<span>{moment.reports_count} reports</span>{/if}
        {#if event.auto_actioned}
          <Badge variant="error" soft size="sm">{event.kind === 'moment_reported' ? 'Auto-hidden' : 'Icon auto-cleared'}</Badge>
        {/if}
      {/if}
      {#if upload}
        {#if upload.failure_reason}<Badge variant="error" soft size="sm">{upload.failure_reason_label}</Badge>{/if}
        {#if upload.failure_message}<span>{upload.failure_message}</span>{/if}
        {#if uploadedText}<span>{uploadedText}</span>{/if}
      {/if}
    </div>
  </div>

  <div class="shrink-0 flex items-center gap-1 flex-wrap justify-end">
    {#if event.kind === 'media_upload_failed'}
      <Link href="/media-uploads" class="link link-hover text-xs">Failed uploads</Link>
    {:else if verdict}
      <Badge variant={verdict.variant} soft={verdict.variant !== 'ghost'} size="sm">{verdict.label}</Badge>
      {#if canUndo && event.kind === 'quest_submission_submitted'}
        <Button variant="ghost" size="xs" icon="undo" disabled={busy} onclick={() => actions.undoForEvent(event)}>Undo</Button>
      {/if}
    {:else if !actionable}
      <!-- nothing left to act on -->
    {:else if event.kind === 'quest_submission_submitted'}
      <Button variant="ghost" size="xs" onclick={() => onOpen(event)}>Review</Button>
      <Button variant="primary" size="xs" disabled={busy} onclick={() => actions.approveSubmission(event)}>Approve</Button>
      <Button variant="ghost" size="xs" disabled={busy} onclick={() => actions.rejectSubmission(event)}>Reject</Button>
      <Button variant="error" outline size="xs" disabled={busy} onclick={() => actions.deleteSubmission(event)}>Delete</Button>
    {:else if event.kind === 'moment_reported'}
      <Button variant="primary" size="xs" disabled={busy} onclick={() => actions.approveMoment(event)}>
        {moment.hidden ? 'Approve & unhide' : 'Approve'}
      </Button>
      <Button variant="error" outline size="xs" disabled={busy} onclick={() => actions.deleteMoment(event)}>Delete</Button>
    {:else if event.kind === 'user_reported' && event.user}
      <Button variant="primary" size="xs" disabled={busy} onclick={() => actions.approveUser(event)}>Approve</Button>
      <Button variant="error" outline size="xs" disabled={busy || !event.user?.has_icon} onclick={() => actions.clearUserIcon(event)}>
        Clear icon
      </Button>
    {/if}
  </div>
</li>
